import React from 'react';
import { Accordion, AccordionDetails, AccordionSummary, Divider, Grid, Card as MuiCard } from '@mui/material';
import { Box, Typography } from 'components/index';
import WhiteFab from '../../components/common/WhiteFab';
import { Edit, ExpandMore } from '@mui/icons-material';
import WarningIcon from '@mui/icons-material/Warning';

export interface CardDataItem {
  value: string | JSX.Element | undefined;
  gridWidth?: number;
}

export type CardData = { [key: string]: string | JSX.Element | undefined | CardDataItem };

export interface CardProps {
  icon?: JSX.Element;
  heading: string | JSX.Element;
  subHeading?: string;
  headerAction?: JSX.Element;
  data: CardData;
  footer?: JSX.Element;
  itemsPerRow?: 1 | 2 | 3 | 4 | 5 | 6;
  spacing?: number;
  backgroundColor?: string;
  isEditMode?: boolean;
  showEditIcon?: boolean;
  onEditClick?: () => void; // Callback function for edit click
  errorFields?: string[];
  disableActions?: boolean;
  collapsible?: boolean;
}

const Card: React.FC<CardProps> = ({
  icon,
  heading,
  subHeading,
  headerAction,
  data,
  footer,
  itemsPerRow = 3,
  spacing = 3,
  backgroundColor,
  showEditIcon,
  onEditClick,
  isEditMode,
  errorFields,
  disableActions,
  collapsible = false,
}) => {
  const gridWidth = 12 / itemsPerRow;

  let actions = null;
  if (!disableActions) {
    actions = (
      <Box display='flex' alignItems='center' gap={1}>
        {headerAction ?? null}
        {showEditIcon && !isEditMode && (
          <WhiteFab
            aria-label='edit'
            icon={<Edit fontSize='small' />}
            textColor='primary'
            onClick={event => {
              event.stopPropagation();
              onEditClick?.();
            }}
            size='small'
          />
        )}
      </Box>
    );
  }

  const cardHeader = (
    <Box display='flex' justifyContent='space-between' width='100%' alignItems='baseline'>
      <Box display='flex' flexDirection='column'>
        <Box display='flex' alignItems='center'>
          {icon}
          <Typography variant='h5' color='primary' sx={{ ml: icon ? 1 : 0 }}>
            {heading}
          </Typography>
        </Box>
        {subHeading && (
          <Typography variant='caption' color='secondary' sx={{ mt: 1 }}>
            {subHeading}
          </Typography>
        )}
      </Box>
      {actions}
    </Box>
  );

  const cardContent = (
    <>
      <Divider sx={{ mb: 2 }} />
      <Grid container spacing={spacing}>
        {Object.entries(data || {}).map(([field, value]) => {
          const highlightField = errorFields?.includes(field) ?? false;
          if (typeof value === 'object' && value !== null && 'value' in value) {
            return renderCardField(field, value.value, value.gridWidth || gridWidth, highlightField);
          } else {
            return renderCardField(field, value, gridWidth, highlightField);
          }
        })}
      </Grid>
      <Box display='flex' alignItems='center' sx={{ marginTop: 2 }}>
        {footer}
      </Box>
    </>
  );

  if (collapsible) {
    return (
      <Accordion sx={{ backgroundColor, px: 2, borderRadius: '10px !important' }} defaultExpanded={isEditMode}>
        <AccordionSummary expandIcon={<ExpandMore />} sx={{ p: 0 }}>
          {cardHeader}
        </AccordionSummary>
        <AccordionDetails sx={{ p: 0, pb: 2 }}>{cardContent}</AccordionDetails>
      </Accordion>
    );
  }

  return (
    <MuiCard elevation={3} sx={{ p: 2, width: '100%', height: '100%', backgroundColor }}>
      {cardHeader}
      {cardContent}
    </MuiCard>
  );
};

export const renderCardField = (field: string, value: string | JSX.Element | undefined, gridWidth: number, highlightField = false) => {
  return field == 'divider' && !value ? (
    <Grid size={{ xs: 12 }} key={field}>
      <Divider sx={{ borderWidth: '1px' }} />
    </Grid>
  ) : (
    <Grid size={{ xs: gridWidth }} key={field}>
      <Box display='flex' flexDirection='column'>
        <Typography variant='caption' color={'text'} fontWeight='medium' textTransform='capitalize'>
          {highlightField && (
            <WarningIcon
              color='warning'
              sx={{
                fontSize: '0.9rem !important',
                mb: 0.3,
                mr: 0.5,
              }}
            />
          )}
          {field}:
        </Typography>
        {value ? (
          typeof value === 'object' ? (
            value
          ) : (
            <Typography variant='body2' fontWeight='regular' sx={{ whiteSpace: 'pre-wrap' }}>
              {value}
            </Typography>
          )
        ) : null}
      </Box>
    </Grid>
  );
};

export default React.memo(Card);
